import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { BrandMarkLink } from "../components/BrandMarkLink";
import { PaperGrain } from "../components/PaperGrain";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { homeForRole } from "../lib/roleHome";

const ERR_COPY = {
  email_taken: "That email is already linked to another account.",
  phone_taken: "That phone number is already linked to another account.",
  invalid_phone: "Phone must be in international format, e.g. +14155551234.",
  invalid_birthday: "Birthday should be in YYYY-MM-DD format.",
};

const friendly = (err) => {
  const code = err?.response?.data?.detail?.error;
  if (code && ERR_COPY[code]) return ERR_COPY[code];
  return "We couldn't save your profile. Please try again.";
};

const Field = ({ label, helper, children, testId }) => (
  <label className="block" data-testid={testId}>
    <span className="block font-body font-semibold text-[11px] tracking-[0.2em] uppercase text-napoli-espresso-soft">
      {label}
    </span>
    {children}
    {helper ? (
      <span className="mt-1 block font-body italic text-[12px] text-napoli-espresso-soft/80">
        {helper}
      </span>
    ) : null}
  </label>
);

const inputCls =
  "mt-2 w-full bg-napoli-cream-warm border border-napoli-gold/40 text-napoli-espresso px-4 py-3 font-body text-[16px] focus:outline-none focus:border-napoli-oxblood transition-colors";

const CompleteProfilePage = () => {
  const navigate = useNavigate();
  const { user, login, updateUser, logout } = useAuth();
  const hasEmail = !!user?.email;
  const hasPhone = !!user?.phone;

  const [firstName, setFirstName] = useState(user?.first_name || "");
  const [lastName, setLastName] = useState(user?.last_name || "");
  const [extra, setExtra] = useState("");
  const [birthday, setBirthday] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [skipping, setSkipping] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e?.preventDefault();
    if (submitting) return;
    setErr(null);
    setSubmitting(true);
    try {
      const body = {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
      };
      if (!hasEmail && extra.trim()) body.email = extra.trim();
      if (!hasPhone && extra.trim()) body.phone = extra.trim();
      if (birthday.trim()) body.birthday = birthday.trim();
      const { data } = await api.post("/auth/complete-profile", body);
      // Persist new token + user
      login(data.token, data.user);
      navigate(homeForRole(data.user?.role), { replace: true });
    } catch (e2) {
      setErr(friendly(e2));
    } finally {
      setSubmitting(false);
    }
  };

  const skip = async () => {
    if (skipping) return;
    setErr(null);
    setSkipping(true);
    try {
      const { data } = await api.post("/auth/skip-profile");
      login(data.token, data.user);
      navigate(homeForRole(data.user?.role), { replace: true });
    } catch (e2) {
      setErr(friendly(e2));
    } finally {
      setSkipping(false);
    }
  };

  return (
    <main
      data-testid="complete-profile-page"
      className="relative min-h-screen bg-napoli-cream text-napoli-espresso flex flex-col items-center px-6 py-16 overflow-hidden"
    >
      <PaperGrain />
      <div className="relative z-10 w-full max-w-[480px]">
        <div className="flex justify-center">
          <BrandMarkLink size="md" theme="light" testId="complete-profile-brand-link" ariaLabel="Napoli — home" />
        </div>

        <div className="mt-10 text-center">
          <p className="font-body font-semibold text-[11px] tracking-[0.3em] uppercase text-napoli-oxblood">
            Quasi pronto
          </p>
          <h1 className="mt-3 font-display font-bold text-[28px] text-napoli-espresso leading-tight">
            Tell us a bit about yourself.
          </h1>
          <p className="mt-3 font-body italic text-[14px] text-napoli-espresso-soft">
            We&rsquo;ll personalise your rewards and remember your birthday.
          </p>
        </div>

        <form onSubmit={submit} className="mt-10 space-y-4">
          <Field label="First name" testId="profile-first-name-field">
            <input
              data-testid="profile-first-name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              autoComplete="given-name"
              required
              className={inputCls}
            />
          </Field>
          <Field label="Last name" testId="profile-last-name-field">
            <input
              data-testid="profile-last-name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              autoComplete="family-name"
              required
              className={inputCls}
            />
          </Field>

          {!hasEmail ? (
            <Field
              label="Email (optional)"
              helper="So we can send you receipts."
              testId="profile-email-field"
            >
              <input
                data-testid="profile-email"
                type="email"
                value={extra}
                onChange={(e) => setExtra(e.target.value)}
                autoComplete="email"
                placeholder="you@example.com"
                className={inputCls}
              />
            </Field>
          ) : !hasPhone ? (
            <Field
              label="Phone (optional)"
              helper="So we can text you receipts and reservations."
              testId="profile-phone-field"
            >
              <input
                data-testid="profile-phone"
                type="tel"
                value={extra}
                onChange={(e) => setExtra(e.target.value)}
                autoComplete="tel"
                placeholder="+14155551234"
                className={inputCls}
              />
            </Field>
          ) : null}

          <Field
            label="Birthday (optional)"
            helper="Get a special bonus on your birthday."
            testId="profile-birthday-field"
          >
            <input
              data-testid="profile-birthday"
              type="date"
              value={birthday}
              onChange={(e) => setBirthday(e.target.value)}
              className={inputCls}
            />
          </Field>

          {err ? (
            <p
              data-testid="profile-error"
              className="border-l-2 border-napoli-oxblood bg-napoli-oxblood/5 px-4 py-3 font-body text-[13px] text-napoli-oxblood-dark"
            >
              {err}
            </p>
          ) : null}

          <button
            type="submit"
            data-testid="profile-submit"
            disabled={submitting || !firstName.trim() || !lastName.trim()}
            className="mt-2 w-full bg-napoli-oxblood text-napoli-cream font-body font-semibold text-[13px] tracking-widest uppercase py-4 hover:bg-napoli-oxblood-dark transition-colors disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
          >
            {submitting ? (
              <>
                <Loader2 size={16} className="animate-spin" /> Saving…
              </>
            ) : (
              "Complete Profile"
            )}
          </button>
        </form>

        <div className="mt-6 text-center">
          <button
            type="button"
            onClick={skip}
            data-testid="profile-skip"
            disabled={skipping}
            className="font-body text-[13px] text-napoli-espresso-soft underline underline-offset-4 hover:text-napoli-oxblood disabled:opacity-60"
          >
            {skipping ? "…" : "Skip for now"}
          </button>
        </div>

        <p className="mt-10 text-center font-body text-[12px] text-napoli-espresso-soft">
          Wrong account?{" "}
          <button
            type="button"
            onClick={() => logout("/login")}
            data-testid="profile-sign-in-differently"
            className="text-napoli-oxblood underline underline-offset-4 hover:text-napoli-oxblood-dark"
          >
            Sign in differently
          </button>
        </p>
      </div>
    </main>
  );
};

export default CompleteProfilePage;
